import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { lessonProgressService } from '../../lib/lessonProgressService';
import { lessonFinishExit } from '../../lib/onboardingStore';
import { recordLessonFinish, setReminderTime, setStreakGoal } from '../../lib/studyStatsStore';
import {
  dailyChallengeProgress,
  levelFromXp,
  localDateKey,
  reminderIcs,
  STREAK_GOALS,
  totalXp,
  weekDots,
  xpBreakdown,
  type StreakGoal,
  type StudyStats,
  type WeekDot,
} from '../../lib/studyStats';
import { useAuth } from '../../contexts/AuthContext';

function downloadIcs(time: string, today: string) {
  const body = reminderIcs(time, today);
  if (!body) return;
  const blob = new Blob([body], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'my-new-start.ics';
  link.click();
  URL.revokeObjectURL(url);
}

export default function LessonFinishPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const path = params.get('path') || '';
  const lesson = params.get('lesson') || '';
  const lessonId = params.get('id') || '';
  const skipped = params.get('skipped') === '1';
  const correct = skipped ? 0 : Math.max(0, Number(params.get('correct') || 0) || 0);
  const [step, setStep] = useState<'xp' | 'streak'>('xp');
  const [stats, setStats] = useState<StudyStats | null>(null);
  const [added, setAdded] = useState(0);
  const [already, setAlready] = useState(false);
  const [total, setTotal] = useState(0);
  const [ready, setReady] = useState(false);
  const today = localDateKey(new Date());
  const breakdown = xpBreakdown(correct, skipped);

  useEffect(() => {
    let cancel = false;
    if (!lessonId || !path) {
      setReady(true);
      return;
    }
    void (async () => {
      await lessonProgressService.saveCompletedLesson(lessonId, path);
      const result = await recordLessonFinish(user?.id ?? null, { lessonId, correctBlanks: correct, skipped });
      if (cancel) return;
      const ids = [...lessonProgressService.getCompletedLessonsFromLocal()];
      setStats(result.stats);
      setAdded(result.added);
      setAlready(result.already || result.repeated);
      setTotal(totalXp(result.stats, ids));
      setReady(true);
    })();
    return () => {
      cancel = true;
    };
  }, [lessonId, path, correct, skipped, user?.id]);

  if (!lessonId || !path) {
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <p className="text-base font-semibold">This lesson finish could not be opened.</p>
        <button type="button" className="mt-4 min-h-[48px] rounded-2xl bg-teal-700 px-5 text-white" onClick={() => navigate('/home')}>
          Home
        </button>
      </div>
    );
  }

  if (!ready || !stats) {
    return <div className="mx-auto max-w-lg px-5 py-16 text-center text-sm text-gray-600">Saving this lesson…</div>;
  }

  const dots = weekDots(today, stats.studyDates, stats.freezeDates);
  const challenge = dailyChallengeProgress(stats.finishes, today);
  const level = levelFromXp(total);

  const chooseGoal = (goal: StreakGoal | null) => {
    setStats(setStreakGoal(goal));
  };

  const chooseTime = (time: string) => {
    setStats(setReminderTime(time || null));
  };

  return (
    <div className="font-myanmar min-h-screen overflow-x-hidden bg-stone-50" data-testid="lesson-done">
      <div className="mx-auto flex min-h-screen w-full max-w-lg min-w-0 flex-col px-5 py-6">
        {step === 'xp' ? (
          <section data-testid="lesson-xp">
            <p className="text-sm font-semibold text-teal-800">Lesson complete</p>
            <h1 className="mt-2 text-3xl font-semibold text-gray-900">XP</h1>
            <ul className="mt-6 space-y-3 text-base text-gray-900">
              <li className="flex justify-between gap-3 rounded-2xl bg-white px-4 py-3">
                <span>Lesson</span>
                <span data-testid="xp-lesson">{breakdown.lesson} XP</span>
              </li>
              <li className="flex justify-between gap-3 rounded-2xl bg-white px-4 py-3">
                <span>Main ideas</span>
                <span data-testid="xp-blanks">{breakdown.blanks} XP</span>
              </li>
              <li className="flex justify-between gap-3 rounded-2xl bg-white px-4 py-3 font-semibold">
                <span>Added now</span>
                <span data-testid="xp-added">{added} XP</span>
              </li>
              <li className="flex justify-between gap-3 rounded-2xl bg-white px-4 py-3">
                <span>Total XP</span>
                <span data-testid="xp-total">{total}</span>
              </li>
            </ul>
            <p className="mt-4 text-lg font-semibold" data-testid="xp-level">Level {level}</p>
            <p className="text-sm text-gray-600">Level goes up every 50 XP.</p>
            {already ? <p className="mt-2 text-sm text-gray-600">This lesson was already counted.</p> : null}
            <button
              type="button"
              className="mt-8 min-h-[48px] w-full rounded-2xl bg-teal-700 text-sm font-semibold text-white"
              data-testid="finish-xp-continue"
              onClick={() => setStep('streak')}
            >
              Continue
            </button>
          </section>
        ) : (
          <section data-testid="lesson-streak">
            <div className="flex items-center gap-4">
              <svg viewBox="0 0 64 80" className="h-16 w-12 shrink-0" data-testid="streak-flame" aria-hidden="true">
                <path d="M32 4c8 12 4 20 10 28 4 6 8 10 8 20a18 18 0 1 1-36 0c0-8 4-14 8-20 6-8 2-16 10-28z" fill="#f59e0b" />
                <path d="M32 34c4 8 2 12 6 16a10 10 0 1 1-20 0c2-4 6-8 8-12 2-4 2-8 6-4z" fill="#fdba74" />
              </svg>
              <div>
                <p className="text-3xl font-semibold text-gray-900" data-testid="streak-count">{stats.current}</p>
                <p className="text-sm text-gray-600">day streak</p>
              </div>
            </div>
            <div className="mt-6 flex justify-between gap-1" data-testid="streak-week">
              {dots.map((dot) => (
                <WeekDay key={dot.date} dot={dot} />
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-800">
              Streak freezes <span className="font-semibold" data-testid="streak-freezes">{stats.freezesLeft}</span>
            </p>
            <p className="mt-6 text-sm font-semibold text-gray-900">Streak goal</p>
            <p className="text-xs text-gray-600">Optional. Pick a length, or leave it unset.</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {STREAK_GOALS.map((goal) => (
                <button
                  key={goal}
                  type="button"
                  className={`min-h-[44px] rounded-full px-4 text-sm font-semibold ${stats.streakGoal === goal ? 'bg-teal-700 text-white' : 'bg-white text-gray-900'}`}
                  data-testid={`streak-goal-${goal}`}
                  aria-pressed={stats.streakGoal === goal}
                  onClick={() => chooseGoal(goal)}
                >
                  {goal}
                </button>
              ))}
              <button
                type="button"
                className="min-h-[44px] rounded-full bg-white px-4 text-sm font-semibold"
                data-testid="streak-goal-none"
                aria-pressed={stats.streakGoal == null}
                onClick={() => chooseGoal(null)}
              >
                None
              </button>
            </div>
            <label className="mt-6 block text-sm font-semibold text-gray-900" htmlFor="reminder-time">
              Reminder time
              <input
                id="reminder-time"
                type="time"
                className="mt-2 block min-h-[48px] w-full rounded-2xl border border-gray-300 bg-white px-3"
                data-testid="reminder-time"
                value={stats.reminderTime || ''}
                onChange={(event) => chooseTime(event.target.value)}
              />
            </label>
            <p className="mt-2 text-sm text-gray-600">This saves a time on this device. It does not send a notification by itself.</p>
            <button
              type="button"
              className="mt-3 min-h-[48px] w-full rounded-2xl border border-gray-300 bg-white text-sm font-semibold disabled:opacity-50"
              data-testid="reminder-ics"
              disabled={!stats.reminderTime}
              onClick={() => stats.reminderTime && downloadIcs(stats.reminderTime, today)}
            >
              Add to calendar
            </button>
            <div className="mt-6 rounded-2xl bg-white px-4 py-3" data-testid="daily-challenge">
              <p className="text-sm font-semibold">Daily challenge</p>
              <p className="mt-1 text-base">{challenge.done} of {challenge.goal} lessons</p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full bg-teal-700"
                  style={{ width: `${Math.min(100, Math.round((challenge.done / challenge.goal) * 100))}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              className="mt-8 min-h-[48px] w-full rounded-2xl bg-teal-700 text-sm font-semibold text-white"
              data-testid="finish-continue"
              onClick={() => navigate(lessonFinishExit(user?.id ?? null, path, lesson))}
            >
              Continue
            </button>
          </section>
        )}
      </div>
    </div>
  );
}

function WeekDay({ dot }: { dot: WeekDot }) {
  const mark = dot.state === 'studied'
    ? 'bg-orange-500'
    : dot.state === 'freeze'
      ? 'border-2 border-sky-500 bg-sky-50'
      : 'border-2 border-gray-300 bg-white';
  return (
    <div className="flex w-8 flex-col items-center gap-1" data-testid="streak-dot" data-state={dot.state} data-date={dot.date}>
      <span className="text-xs text-gray-500">{dot.label}</span>
      <span className={`h-6 w-6 rounded-full ${mark}`} />
    </div>
  );
}
